import React, { useState, useEffect } from "react";
import { Col, Row, Spinner } from "reactstrap";
import { H3, H5 } from "../../../../AbstractElements";
import axios from "axios";

// Submission row attached per job by the backend (joined submissions+candidates).
interface JobSubmission {
  submissionId: number;
  status: string | null;
  submittedAt: string | null;
  candidateId: number;
  firstName: string | null;
  lastName: string | null;
}

// Response shape from /open-jobs (HDM-DB-backed, flat columns).
interface OpenJobData {
  id: number;
  title: string;
  status: string;
  projected_revenue: number | null;
  ownerId: number | null;
  ownerName: string | null;
  clientId: number | null;
  clientName: string | null;
  notes: string | null;     // last update note text
  notesDate: string | null; // timestamp of that note
  description: string | null; // job description (HTML stripped server-side)
  // Optional: older backend builds may omit this field. Always normalize
  // to an array via `job.submissions ?? []` at the read site.
  submissions?: JobSubmission[];
}
interface StatusGroup {
  status: string;
  totalCustomFloat3: number;
  jobCount: number;
}
interface OpenJobsReportProps {
  filterStatus?: string; // Optional prop for filtering by status
  reportTitle?: string; // Optional prop for custom report title
}

const OpenJobsReport: React.FC<OpenJobsReportProps> = ({
  filterStatus,
  reportTitle,
}) => {
  const [data, setData] = useState<OpenJobData[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof OpenJobData;
    direction: "asc" | "desc";
  } | null>(null);

  const formatterUSD = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  const fetchData = async () => {
    setLoading(true);
    // Map the filterStatus prop to the new flat ?status=... query param.
    // Omit the param entirely to get all open-pipeline statuses (the backend default).
    const params: Record<string, string> = {};
    if (filterStatus === "Top Level") {
      params.status = "Prospecting,Identified";
    } else if (filterStatus === "Validated") {
      params.status = "Validated";
    }
    try {
      console.log("Fetching open jobs with params:", params);
      const response = await axios.get("/open-jobs", { params });

      // Sort jobs by owner name (case-insensitive, nulls last).
      const sortedData = (response.data as OpenJobData[]).slice().sort((a, b) => {
        const an = (a.ownerName || "").toLowerCase();
        const bn = (b.ownerName || "").toLowerCase();
        if (an === bn) return 0;
        if (!an) return 1;
        if (!bn) return -1;
        return an < bn ? -1 : 1;
      });

      setData(sortedData);
      setLoading(false);
    } catch (err: any) {
      console.error("Error fetching data:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const sortData = (key: keyof OpenJobData) => {
    if (!data) return;

    let direction: "asc" | "desc" = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }

    const sortedData = () => {
      if (!data || !sortConfig?.key) return data || [];

      return [...data].sort((a, b) => {
        const key = sortConfig.key as keyof OpenJobData;
        const aValue = a[key];
        const bValue = b[key];

        if (
          aValue === undefined ||
          bValue === undefined ||
          aValue === null ||
          bValue === null
        )
          return 0;
        if ((aValue ?? "") < (bValue ?? ""))
          return sortConfig.direction === "asc" ? -1 : 1;
        if ((aValue ?? "") > (bValue ?? ""))
          return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    };

    setData(sortedData);
    setSortConfig({ key, direction });
  };
  const groupByStatus = (): StatusGroup[] => {
    if (!data) return [];

    const groupsMap: { [key: string]: { total: number; count: number } } = {};

    data.forEach((job) => {
      const status = job.status;
      const revenue = Number(job.projected_revenue) || 0;
      if (groupsMap[status]) {
        groupsMap[status].total += revenue;
        groupsMap[status].count += 1;
      } else {
        groupsMap[status] = { total: revenue, count: 1 };
      }
    });

    // Convert the groupsMap to an array of StatusGroup
    const statusGroups: StatusGroup[] = Object.entries(groupsMap).map(
      ([status, { total, count }]) => ({
        status,
        totalCustomFloat3: total,
        jobCount: count,
      }),
    );

    // Sort the groups in descending order based on totalCustomFloat3
    statusGroups.sort((a, b) => b.totalCustomFloat3 - a.totalCustomFloat3);

    return statusGroups;
  };

  // Get the grouped data
  const statusGroups: StatusGroup[] = groupByStatus();

  if (loading) {
    return (
      <>
        <div>Pulling fresh data from Bullhorn.</div>
        <div>Please wait 30 seconds… Make IT Happen today!</div>
        <Spinner color='primary' />
      </>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <Col xs='12'>
      <style>{`
        .open-jobs-list {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          font-size: 1rem;
          text-align: left;
        }
        .open-jobs-list th, .open-jobs-list td {
          padding: 12px 15px;
          border: 1px solid #ddd;
        }
        .open-jobs-list th {
          background-color: #f4f4f4;
          font-weight: bold;
        }
        .open-jobs-list .even-row {
          background-color: #f9f9f9;
        }
        .open-jobs-list .odd-row {
          background-color: #fff;
        }
        .open-jobs-list tr:hover {
          background-color: #f1f1f1;
        }
        .candidate-list {
          padding-left: 20px;
          list-style: none;
          margin: 10px 0;
        }
        .candidate-list li {
          margin-bottom: 5px;
        }
        .status-groups-table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          font-size: 1rem;
          text-align: left;
        }
        .status-groups-table th, .status-groups-table td {
          padding: 12px 15px;
          border: 1px solid #ddd;
        }
        .status-groups-table th {
          background-color: #e2e2e2;
          font-weight: bold;
        }
        .no-submissions {
            color: gray;
        }
      `}</style>
      <Row>
        <Col xs='12'>
          <H3 className='font-primary'>{reportTitle}</H3>
        </Col>
      </Row>
      <Row>
        <Col xs='12'>
          <H5 className='font-primary'>Total</H5>
          <p>Total Open Jobs: {data?.length || 0}</p>
          {statusGroups.map((group) => (
            <p key={group.status}>
              {group.status} Projected Revenue:{" "}
              {formatterUSD.format(group.totalCustomFloat3)}
            </p>
          ))}
        </Col>
      </Row>

      <Row>
        <Col xs='12'>
          <table className='open-jobs-list'>
            <thead>
              <tr>
                <th>Job Name</th>
                <th onClick={() => sortData("id")}>
                  Job Number (Click to view)
                </th>
                <th onClick={() => sortData("clientName")}>Client</th>
                <th onClick={() => sortData("ownerName")}>Account Manager</th>
                <th onClick={() => sortData("status")}>Status</th>
                <th onClick={() => sortData("projected_revenue")}>
                  Projected Revenue
                </th>
                {filterStatus === "Validated" ? (
                  <th>Submissions (Click to view Candidate)</th>
                ) : (
                  ""
                )}
                <th>Last Note</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((job, index) => (
                <tr
                  key={job.id}
                  className={index % 2 === 0 ? "even-row" : "odd-row"}
                >
                  <td>{job.title}</td>
                  <td>
                    <a
                      target='_blank'
                      href={`https://cls44.bullhornstaffing.com/BullhornSTAFFING/OpenWindow.cfm?Entity=JobOrder&id=${job.id}`}
                      aria-label={`Job Order ${job.id}`}
                      rel='noreferrer'
                    >
                      {job.id}
                    </a>
                  </td>
                  <td>{job.clientName ?? "—"}</td>
                  <td>{job.ownerName ?? "—"}</td>
                  <td>{job.status}</td>
                  <td>
                    {formatterUSD.format(Number(job.projected_revenue) || 0)}
                  </td>

                  {filterStatus === "Validated" ? (
                    <td>
                      <ul className='candidate-list'>
                        {(() => {
                          const subs = job.submissions ?? [];
                          if (subs.length === 0) {
                            return (
                              <li className='no-submissions'>
                                No candidates available
                              </li>
                            );
                          }
                          return subs.map((sub) => (
                            <li key={sub.submissionId}>
                              <a
                                target='_blank'
                                href={`https://cls44.bullhornstaffing.com/BullhornSTAFFING/OpenWindow.cfm?Entity=Candidate&id=${sub.candidateId}`}
                                aria-label={`Candidate ${sub.candidateId}`}
                                rel='noreferrer'
                              >
                                {sub.firstName} {sub.lastName}
                              </a>
                            </li>
                          ));
                        })()}
                      </ul>
                    </td>
                  ) : (
                    ""
                  )}
                  <td>{job.notes ?? "No Update Note Available"}</td>
                  <td style={{ whiteSpace: "pre-wrap" }}>
                    {job.description ?? "No Description Available"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Col>
      </Row>
    </Col>
  );
};

export default OpenJobsReport;
